/**
 * Persistent XMPP-over-WebSocket connection using Strophe.js.
 *
 * Ported from `src/chat/xmppService.js` to TypeScript and wired up to the
 * project's `env.contact` configuration. Provides:
 *   • connect / disconnect
 *   • sendMessage / sendGroupMessage              (plain text)
 *   • sendMediaMessage / sendGroupMediaMessage    (image / audio / video)
 *   • sendDisplayedMarker                         (XEP-0333 read receipt)
 *
 * Callers subscribe via the `callbacks` object passed to `connect`.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
// strophe.js ships its own typings.
import { Strophe, $msg, $pres, $iq } from 'strophe.js';
import { contact } from '@/config/env';
import {
  decodeMessageBody,
  encodeMediaBody,
  KNOWN_SUBJECTS,
  type ChatSubject,
  type MediaPayload,
} from '@/features/messages/mediaUtils';

// ─── Configuration ──────────────────────────────────────────────────────────

const WS_URL = contact.xmppServiceUrl;
const DOMAIN = contact.xmppDomain;
const GROUP_DOMAIN = contact.xmppMucDomain;

/**
 * A fixed `/web` resource makes every browser tab use the same full JID.
 * ejabberd then resolves the collision by replacing the older socket, whose
 * reconnect replaces the newer socket again, creating an endless loop.
 * Keep one resource for this page lifetime, but make it unique across tabs,
 * refreshes and devices. The prefix still identifies this as the web client.
 */
const RESOURCE = `web-${
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`
}`;

// ─── Public types ───────────────────────────────────────────────────────────

export interface IncomingChatMessage {
  from: string; // bare peer JID
  type: ChatSubject | string;
  text: string;
  mediaUrl: string | null;
  thumbUrl: string | null;
  mediaWidth: number | null;
  mediaHeight: number | null;
  duration?: number | null;
  mediaSizeInByte?: number | null;
  sender: 'incoming' | 'outgoing';
  date: string;
  rawTimestamp: number;
  id: string | null;
  isCarbon?: boolean;
}

export interface IncomingGroupMessage extends Omit<IncomingChatMessage, 'from'> {
  groupId: string;
  senderId: string;
  isOwn: boolean;
  upgradeRequired?: boolean;
}

/**
 * MUC lifecycle event — destroy / member subscribe / member unsubscribe.
 * Mirrors Android `ChattingHelper.getChatMessageModel` group branch
 * (TYPE_GROUP_DELETED / TYPE_GROUP_PARTICIPANT_ADDED / TYPE_GROUP_PARTICIPANT_DELETED).
 */
export type GroupSystemEventKind = 'destroyed' | 'participant_added' | 'participant_removed';

export interface GroupSystemEvent {
  groupId: string;
  kind: GroupSystemEventKind;
  /** XMPP user/jid resource of the affected member (for participant events). */
  affectedNick?: string;
  /** Numeric userId parsed from the affected nick when possible. */
  affectedUserId?: string;
  /**
   * userId of who *performed* the action (parsed from `from.resource`). Per
   * spec §7.2 this is the publisher of the MUCSub subscribers event, or the
   * destroyer for `destroyed` events. Used by the client to:
   *   • detect creator's own subscribe (publisher == affected == me → owner)
   *   • render "{publisherName} added/removed {affectedName}"
   */
  publisherUserId?: string;
  /** Free-form reason if the server provided one (destroy events). */
  reason?: string;
  rawTimestamp: number;
  id: string | null;
}

export interface XmppCallbacks {
  onConnected?: () => void;
  onDisconnected?: (reason?: string) => void;
  /** Stanza was written to the active XMPP connection (single-tick state). */
  onSent?: (msgId: string) => void;
  onMessage?: (msg: IncomingChatMessage) => void;
  onGroupMessage?: (msg: IncomingGroupMessage) => void;
  onGroupSystemEvent?: (evt: GroupSystemEvent) => void;
  onDelivered?: (
    msgId: string,
    from: string,
    opts?: { isGroup?: boolean; senderId?: string },
  ) => void;
  onRead?: (msgId: string, from: string, opts?: { isGroup?: boolean; senderId?: string }) => void;
  onPresence?: (bareJid: string, isOnline: boolean) => void;
  onTyping?: (bareJid: string, isTyping: boolean) => void;
  /**
   * Spec §10: server-pushed blocking events for the current user — sent
   * when another logged-in device blocks/unblocks someone. `jids` is the
   * list of affected bare JIDs (typically a single user).
   */
  onBlockingPush?: (action: 'block' | 'unblock', jids: string[]) => void;
}

// ─── Module state ───────────────────────────────────────────────────────────

let _connection: any = null;
let _callbacks: XmppCallbacks = {};
let _jid = '';
let _userId = '';
let _password = '';
let _shouldReconnect = false;
let _reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let _reconnectAttempt = 0;
let _isConnecting = false;
let _hiddenAt = 0;
let _restartRequested = false;
let _restartFallbackTimer: ReturnType<typeof setTimeout> | null = null;
const _pendingStanzas: Array<{ stanza: Element; msgId?: string }> = [];
const _resendReceipts: Record<string, string> = {};

const STATUS_LABELS: Record<number, string> = {
  [Strophe.Status.ERROR]: 'ERROR',
  [Strophe.Status.CONNECTING]: 'CONNECTING',
  [Strophe.Status.CONNFAIL]: 'CONNFAIL',
  [Strophe.Status.AUTHENTICATING]: 'AUTHENTICATING',
  [Strophe.Status.AUTHFAIL]: 'AUTHFAIL',
  [Strophe.Status.CONNECTED]: 'CONNECTED',
  [Strophe.Status.DISCONNECTED]: 'DISCONNECTED',
  [Strophe.Status.DISCONNECTING]: 'DISCONNECTING',
  [Strophe.Status.ATTACHED]: 'ATTACHED',
  [Strophe.Status.REDIRECT]: 'REDIRECT',
  [Strophe.Status.CONNTIMEOUT]: 'CONNTIMEOUT',
};

// ─── Helpers ────────────────────────────────────────────────────────────────

const barePeer = (fullJid?: string | null): string => (fullJid ?? '').split('/')[0] ?? '';

const formatTimestamp = (microTs?: number): string => {
  if (!microTs) return 'Just now';
  const d = new Date(Number(microTs) / 1000);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
};

const newMsgId = () => `msg-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const utf8B64 = (text: string) => btoa(unescape(encodeURIComponent(text)));

function _openConnection() {
  if (!_shouldReconnect || !_jid || !_password) return;
  if (_connection?.connected || _isConnecting) return;
  if (_reconnectTimer) {
    clearTimeout(_reconnectTimer);
    _reconnectTimer = null;
  }
  const nextConnection = new Strophe.Connection(WS_URL, { protocol: 'wss' });
  _connection = nextConnection;
  _isConnecting = true;
  // Never reset/reuse a Strophe instance while its `_connect_cb` is active.
  // Old sockets can report a late status after replacement, so bind the
  // callback to this exact generation and ignore stale notifications.
  nextConnection.connect(_jid, _password, (status: number, condition?: string) => {
    if (_connection !== nextConnection) return;
    _onStatusChange(status, condition);
  });
}

function _restartConnection() {
  if (!_shouldReconnect) return;
  if (!_connection?.connected && !_isConnecting) {
    _scheduleReconnect(true);
    return;
  }
  if (_restartRequested) return;
  _restartRequested = true;
  const closingConnection = _connection;
  try {
    closingConnection.disconnect('reconnect');
  } catch {
    // A sleeping browser may already have lost the underlying socket.
  }
  // Normally Strophe reports DISCONNECTED and reconnects below. If the dead
  // browser socket never completes its close handshake, release it after a
  // short grace period before opening the replacement.
  _restartFallbackTimer = setTimeout(() => {
    if (!_restartRequested || _connection !== closingConnection) return;
    _restartRequested = false;
    _restartFallbackTimer = null;
    _connection = null;
    _isConnecting = false;
    _scheduleReconnect(true);
  }, 3_000);
}

function _clearRestartState() {
  _restartRequested = false;
  if (_restartFallbackTimer) {
    clearTimeout(_restartFallbackTimer);
    _restartFallbackTimer = null;
  }
}

function _closeConnection(connection: any, reason?: string) {
  if (!connection) return;
  try {
    connection.disconnect(reason);
  } catch {
    // The browser socket may already be closed.
  }
}

function _scheduleReconnect(immediate = false) {
  if (!_shouldReconnect || _reconnectTimer || _isConnecting) return;
  const delay = immediate ? 0 : Math.min(30_000, 1_000 * 2 ** _reconnectAttempt++);
  _reconnectTimer = setTimeout(_openConnection, delay);
}

function _sendOrQueue(stanza: Element, msgId?: string) {
  if (_connection?.connected) {
    _connection.send(stanza);
    if (msgId) queueMicrotask(() => _callbacks.onSent?.(msgId));
    return;
  }
  _pendingStanzas.push({ stanza, msgId });
  _scheduleReconnect(true);
}

function _flushPendingStanzas() {
  if (!_connection?.connected) return;
  while (_pendingStanzas.length) {
    const pending = _pendingStanzas.shift();
    if (pending) {
      _connection.send(pending.stanza);
      if (pending.msgId) {
        const sentId = pending.msgId;
        queueMicrotask(() => _callbacks.onSent?.(sentId));
      }
    }
  }
}

function _resumeConnection(event: Event) {
  if (event.type === 'visibilitychange' && document.visibilityState === 'hidden') {
    _hiddenAt = Date.now();
    return;
  }
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
  const wasIdle = _hiddenAt > 0 && Date.now() - _hiddenAt >= 30_000;
  _hiddenAt = 0;
  // Browsers can leave Strophe's `connected` flag true even though a sleeping
  // device's WebSocket has been silently discarded. Recreate it after a
  // meaningful background period (or whenever the network comes back).
  if (wasIdle || event.type === 'online') {
    _restartConnection();
  } else if (!_connection?.connected) {
    _scheduleReconnect(true);
  }
}

/**
 * Spec §6.1: when a message carries `<delay xmlns="urn:xmpp:delay" stamp="…"/>`
 * (typically because the server stored & forwarded it while the recipient was
 * offline) use that ISO-8601 stamp as the message timestamp instead of "now".
 * Returns a microsecond epoch when a delay is present, or `undefined` so the
 * caller can fall back to its own clock.
 */
function _readDelayStampMicros(stanza: Element): number | undefined {
  const delayEls = stanza.getElementsByTagNameNS('urn:xmpp:delay', 'delay');
  for (let i = 0; i < delayEls.length; i += 1) {
    const stamp = delayEls[i].getAttribute('stamp');
    if (!stamp) continue;
    const ms = Date.parse(stamp);
    if (Number.isFinite(ms) && ms > 0) return ms * 1000;
  }
  return undefined;
}

/** Delay-aware timestamp — returns micros from `<delay>` or `Date.now()*1000`. */
function _extractStanzaTimestampMicros(stanza: Element): number {
  return _readDelayStampMicros(stanza) ?? Date.now() * 1000;
}

// ─── Stanza handlers ────────────────────────────────────────────────────────

function _onIncomingMessage(stanza: Element): boolean {
  const fromAttr = stanza.getAttribute('from') ?? '';
  const from = barePeer(fromAttr);
  const bodyEl = stanza.getElementsByTagName('body')[0];
  if (!bodyEl) return true;

  // Skip stanzas wrapped in carbons; those go through _onCarbonMessage.
  const carbonNs = 'urn:xmpp:carbons:2';
  if (
    stanza.getElementsByTagNameNS(carbonNs, 'sent')[0] ||
    stanza.getElementsByTagNameNS(carbonNs, 'received')[0]
  ) {
    return true;
  }

  const subject = stanza.getElementsByTagName('subject')[0]?.textContent ?? 'chat_text';
  if (!KNOWN_SUBJECTS.includes(subject as ChatSubject)) return true;

  const decoded = decodeMessageBody(bodyEl.textContent || '', subject);
  const msgId = stanza.getAttribute('id');
  const now = _extractStanzaTimestampMicros(stanza);

  // XEP-0184 delivery receipt
  if (msgId && stanza.getElementsByTagName('request').length > 0) {
    _connection.send(
      $msg({ to: fromAttr, type: 'chat' })
        .c('received', { xmlns: 'urn:xmpp:receipts', id: msgId })
        .tree(),
    );
  }
  // XEP-0333 received marker
  if (msgId) {
    _connection.send(
      $msg({ to: fromAttr, type: 'chat' })
        .c('received', { xmlns: 'urn:xmpp:chat-markers:0', id: msgId })
        .tree(),
    );
  }

  _callbacks.onMessage?.({
    from,
    type: decoded.type,
    text: decoded.text,
    mediaUrl: decoded.mediaUrl,
    thumbUrl: decoded.thumbUrl,
    mediaWidth: decoded.mediaWidth,
    mediaHeight: decoded.mediaHeight,
    duration: decoded.duration,
    mediaSizeInByte: decoded.mediaSizeInByte,
    sender: 'incoming',
    date: formatTimestamp(now),
    rawTimestamp: now,
    id: msgId,
  });
  return true;
}

function _onGroupMessage(stanza: Element): boolean {
  const fromFull = stanza.getAttribute('from') ?? '';
  const [groupJid, resource = ''] = fromFull.split('/');
  const groupId = (groupJid ?? '').split('@')[0] ?? '';
  const msgId = stanza.getAttribute('id');
  const now = _extractStanzaTimestampMicros(stanza);

  // ── MUC lifecycle detection (Android `ChattingHelper.getChatMessageModel`
  // group branch). These stanzas may arrive WITHOUT a <body>, so they have
  // to be checked before the regular body short-circuit below.

  // 1. Group destroyed: <x xmlns="http://jabber.org/protocol/muc#user"><destroy/></x>
  // OR the simpler MUCSub form: <destroy xmlns="http://jabber.org/protocol/muc#user"/>
  // directly on the message (spec §7.2 / Appendix).
  const directDestroy = stanza.getElementsByTagNameNS(
    'http://jabber.org/protocol/muc#user',
    'destroy',
  )[0];
  if (directDestroy && directDestroy.parentNode === stanza) {
    const reasonEl = directDestroy.getElementsByTagName('reason')[0];
    const destroyer = resource && resource.includes('@') ? resource.split('@')[0] : resource;
    _callbacks.onGroupSystemEvent?.({
      groupId,
      kind: 'destroyed',
      reason: reasonEl?.textContent ?? directDestroy.getAttribute('reason') ?? undefined,
      publisherUserId: destroyer || undefined,
      rawTimestamp: now,
      id: msgId,
    });
    return true;
  }
  const mucUserEls = stanza.getElementsByTagNameNS('http://jabber.org/protocol/muc#user', 'x');
  for (let i = 0; i < mucUserEls.length; i += 1) {
    const destroy = mucUserEls[i].getElementsByTagName('destroy')[0];
    if (destroy) {
      const reasonEl = destroy.getElementsByTagName('reason')[0];
      const destroyer = resource && resource.includes('@') ? resource.split('@')[0] : resource;
      _callbacks.onGroupSystemEvent?.({
        groupId,
        kind: 'destroyed',
        reason: reasonEl?.textContent ?? destroy.getAttribute('reason') ?? undefined,
        publisherUserId: destroyer || undefined,
        rawTimestamp: now,
        id: msgId,
      });
      return true;
    }
  }

  // 2. MucSub member events (ejabberd extension): a pubsub-event wrapper
  // referencing the `urn:xmpp:mucsub:nodes:subscribers` node, with a
  // <subscribe nick="…"/> or <unsubscribe nick="…"/> child inside the
  // <item>. Per spec §7.2:
  //   • `from.resource` (the room nick) = publisher (who performed the action)
  //   • `jid="{affectedUserId}@localhost"` = the affected member
  //   • `nick="{affectedUserId}"` = same id as string (fallback)
  const eventEls = stanza.getElementsByTagNameNS(
    'http://jabber.org/protocol/pubsub#event',
    'event',
  );
  const publisherUserId = resource && resource.includes('@') ? resource.split('@')[0] : resource;
  for (let i = 0; i < eventEls.length; i += 1) {
    const items = eventEls[i].getElementsByTagName('items')[0];
    if (!items || items.getAttribute('node') !== 'urn:xmpp:mucsub:nodes:subscribers') continue;
    const subscribe = items.getElementsByTagName('subscribe')[0];
    const unsubscribe = items.getElementsByTagName('unsubscribe')[0];
    const node = subscribe ?? unsubscribe;
    if (!node) continue;
    const jidAttr = node.getAttribute('jid') ?? '';
    const nick = node.getAttribute('nick') ?? '';
    // Spec §7.2 + Implementation Notes: `nick` is always present (even in
    // anonymous rooms where `jid` is omitted for non-moderators), so it is
    // the primary identifier. Fall back to the bare-JID localpart only when
    // nick is empty.
    const affectedUserId =
      (nick && (nick.includes('@') ? nick.split('@')[0] : nick)) ||
      (jidAttr ? jidAttr.split('@')[0] : '');
    _callbacks.onGroupSystemEvent?.({
      groupId,
      kind: subscribe ? 'participant_added' : 'participant_removed',
      affectedNick: nick || jidAttr || undefined,
      affectedUserId: affectedUserId || undefined,
      publisherUserId: publisherUserId || undefined,
      rawTimestamp: now,
      id: msgId,
    });
    return true;
  }

  const bodyEl = stanza.getElementsByTagName('body')[0];
  if (!bodyEl) return true;
  const subject = stanza.getElementsByTagName('subject')[0]?.textContent ?? 'chat_text';
  const upgradeRequired = !KNOWN_SUBJECTS.includes(subject as ChatSubject);

  const senderId = resource;

  const decoded = upgradeRequired
    ? {
        text: bodyEl.textContent || '',
        type: subject,
        mediaUrl: null,
        thumbUrl: null,
        mediaWidth: null,
        mediaHeight: null,
        duration: null,
        mediaSizeInByte: null,
      }
    : decodeMessageBody(bodyEl.textContent || '', subject);

  const isOwn = senderId === _userId;

  _callbacks.onGroupMessage?.({
    groupId,
    senderId,
    isOwn,
    type: subject,
    text: decoded.text,
    mediaUrl: decoded.mediaUrl,
    thumbUrl: decoded.thumbUrl,
    mediaWidth: decoded.mediaWidth,
    mediaHeight: decoded.mediaHeight,
    duration: decoded.duration ?? null,
    mediaSizeInByte: decoded.mediaSizeInByte ?? null,
    sender: isOwn ? 'outgoing' : 'incoming',
    date: formatTimestamp(now),
    rawTimestamp: now,
    id: msgId,
    upgradeRequired,
  });
  return true;
}

function _onCarbonMessage(stanza: Element): boolean {
  const ns = 'urn:xmpp:carbons:2';
  const sentEl = stanza.getElementsByTagNameNS(ns, 'sent')[0];
  const receivedEl = stanza.getElementsByTagNameNS(ns, 'received')[0];
  const wrapper = sentEl || receivedEl;
  if (!wrapper) return true;
  const isOutgoing = !!sentEl;

  const forwarded = wrapper.getElementsByTagName('forwarded')[0];
  if (!forwarded) return true;
  const innerMsg = forwarded.getElementsByTagName('message')[0];
  if (!innerMsg) return true;

  const innerType = innerMsg.getAttribute('type');
  const bodyEl = innerMsg.getElementsByTagName('body')[0];
  if (!bodyEl) return true;
  const subject = innerMsg.getElementsByTagName('subject')[0]?.textContent ?? 'chat_text';
  if (!KNOWN_SUBJECTS.includes(subject as ChatSubject)) return true;

  const msgId = innerMsg.getAttribute('id');
  // Carbons may carry the original `<delay>` on either the inner message or
  // the outer forwarded wrapper — try inner first, fall back to outer, then
  // wall-clock.
  const now = _readDelayStampMicros(innerMsg) ?? _readDelayStampMicros(stanza) ?? Date.now() * 1000;

  if (innerType === 'groupchat') {
    const fromFull = innerMsg.getAttribute('from') ?? '';
    const toFull = innerMsg.getAttribute('to') ?? '';
    const roomFull = isOutgoing ? barePeer(toFull) : barePeer(fromFull);
    const groupId = (roomFull ?? '').split('@')[0] ?? '';
    const senderId = isOutgoing ? _userId : (fromFull.split('/')[1] ?? '');
    const decoded = decodeMessageBody(bodyEl.textContent || '', subject);
    const isOwn = isOutgoing || senderId === _userId;

    _callbacks.onGroupMessage?.({
      groupId,
      senderId,
      isOwn,
      type: subject,
      text: decoded.text,
      mediaUrl: decoded.mediaUrl,
      thumbUrl: decoded.thumbUrl,
      mediaWidth: decoded.mediaWidth,
      mediaHeight: decoded.mediaHeight,
      duration: decoded.duration ?? null,
      mediaSizeInByte: decoded.mediaSizeInByte ?? null,
      sender: isOwn ? 'outgoing' : 'incoming',
      date: formatTimestamp(now),
      rawTimestamp: now,
      id: msgId,
      isCarbon: true,
    });
    return true;
  }

  if (innerType !== 'chat') return true;

  const decoded = decodeMessageBody(bodyEl.textContent || '', subject);
  const fromFull = innerMsg.getAttribute('from') ?? '';
  const toFull = innerMsg.getAttribute('to') ?? '';
  const peerJid = isOutgoing ? barePeer(toFull) : barePeer(fromFull);

  _callbacks.onMessage?.({
    from: peerJid,
    type: decoded.type,
    text: decoded.text,
    mediaUrl: decoded.mediaUrl,
    thumbUrl: decoded.thumbUrl,
    mediaWidth: decoded.mediaWidth,
    mediaHeight: decoded.mediaHeight,
    duration: decoded.duration,
    mediaSizeInByte: decoded.mediaSizeInByte,
    sender: isOutgoing ? 'outgoing' : 'incoming',
    date: formatTimestamp(now),
    rawTimestamp: now,
    id: msgId,
    isCarbon: true,
  });
  return true;
}

/**
 * MUCSub pubsub-wrapped message dispatcher (spec §7.1 & §7.2).
 *
 * MUCSub delivers room events inside a `<event xmlns="…pubsub#event">
 * <items node="urn:xmpp:mucsub:nodes:{messages|subscribers}"><item>…inner…
 * </item></items></event>` envelope. The outer `<message>` has no `type`
 * attribute (or `type="headline"`) so it doesn't match the `type='groupchat'`
 * filter on `_onGroupMessage`. This dispatcher unwraps and re-routes.
 *
 * Supported nodes:
 *   • urn:xmpp:mucsub:nodes:messages    → unwrap inner <message type="groupchat">
 *                                          and feed to _onGroupMessage
 *   • urn:xmpp:mucsub:nodes:subscribers → handle inline (subscribe/unsubscribe
 *                                          system events, see §7.2)
 */
function _onMucSubWrappedEvent(stanza: Element): boolean {
  // Skip if this stanza is already being processed by another handler (the
  // type='groupchat' handler would have matched bare groupchat messages).
  if (stanza.getAttribute('type') === 'groupchat') return true;

  const eventEls = stanza.getElementsByTagNameNS(
    'http://jabber.org/protocol/pubsub#event',
    'event',
  );
  if (!eventEls.length) return true;

  for (let i = 0; i < eventEls.length; i += 1) {
    const itemsEl = eventEls[i].getElementsByTagName('items')[0];
    if (!itemsEl) continue;
    const node = itemsEl.getAttribute('node') ?? '';

    // 1) Wrapped chat message
    if (node === 'urn:xmpp:mucsub:nodes:messages') {
      const items = itemsEl.getElementsByTagName('item');
      for (let j = 0; j < items.length; j += 1) {
        const inner = items[j].getElementsByTagName('message')[0];
        if (inner && inner.getAttribute('type') === 'groupchat') {
          // Wrapped deliveries may keep the canonical stanza id only on the
          // pubsub <item>. Receipts/dedup must use that original id.
          if (!inner.getAttribute('id')) {
            const itemId = items[j].getAttribute('id');
            if (itemId) inner.setAttribute('id', itemId);
          }
          _onGroupMessage(inner);
        }
      }
      return true;
    }

    // 2) Member subscribe / unsubscribe event — delegate to _onGroupMessage
    //    so its existing pubsub#event detection branch fires (it already
    //    handles both `urn:xmpp:mucsub:nodes:subscribers` shapes).
    if (node === 'urn:xmpp:mucsub:nodes:subscribers') {
      _onGroupMessage(stanza);
      return true;
    }
  }
  return true;
}

function _onDeliveryReceipt(stanza: Element): boolean {
  const received = stanza.getElementsByTagName('received')[0];
  if (!received) return true;
  if (received.getAttribute('xmlns') !== 'urn:xmpp:receipts') return true;
  const msgId = received.getAttribute('id');
  const from = barePeer(stanza.getAttribute('from'));
  if (msgId) _callbacks.onDelivered?.(msgId, from);
  return true;
}

function _onChatMarker(stanza: Element): boolean {
  const ns = 'urn:xmpp:chat-markers:0';
  const fromAttr = stanza.getAttribute('from') ?? '';
  const from = barePeer(fromAttr);
  // For groupchat markers the `from` is `{room}/{readerUserId}` — we need
  // the resource to attribute the receipt to a specific group member
  // (chat parity gap §5.4 / item #9).
  const isGroup = stanza.getAttribute('type') === 'groupchat';
  const readerId = isGroup ? (fromAttr.split('/')[1] ?? '') : '';
  const opts = isGroup ? { isGroup: true, senderId: readerId } : undefined;

  const displayed = stanza.getElementsByTagName('displayed')[0];
  if (displayed?.getAttribute('xmlns') === ns) {
    const msgId = displayed.getAttribute('id');
    if (msgId) _callbacks.onRead?.(msgId, from, opts);
    return true;
  }
  const received = stanza.getElementsByTagName('received')[0];
  if (received?.getAttribute('xmlns') === ns) {
    const msgId = received.getAttribute('id');
    if (msgId) _callbacks.onDelivered?.(msgId, from, opts);
  }
  return true;
}

function _onPresenceStanza(stanza: Element): boolean {
  const fromAttr = stanza.getAttribute('from');
  if (!fromAttr) return true;
  const bareFrom = barePeer(fromAttr);
  const type = stanza.getAttribute('type');

  if (type === 'subscribe') {
    _connection.send($pres({ to: bareFrom, type: 'subscribed' }).tree());
    _connection.send($pres({ to: bareFrom, type: 'subscribe' }).tree());
    return true;
  }
  if (type === 'subscribed' || type === 'unsubscribed') return true;
  if (bareFrom === barePeer(_jid)) return true;

  const isOnline = !type || type === 'available';
  _callbacks.onPresence?.(bareFrom, isOnline);
  return true;
}

function _onChatState(stanza: Element): boolean {
  // Chat-state notifications (XEP-0085) are only meaningful for 1‑to‑1
  // chats here. For `groupchat` stanzas the `from` is `{room}/{nick}`, so
  // `barePeer` would yield the room JID and we'd mark the entire group as
  // typing — surface this as "no typing" for groups.
  const msgType = stanza.getAttribute('type') ?? 'chat';
  if (msgType === 'groupchat') return true;
  const from = barePeer(stanza.getAttribute('from'));
  if (!from) return true;
  const ns = 'http://jabber.org/protocol/chatstates';
  const composing = stanza.getElementsByTagNameNS(ns, 'composing')[0];
  const paused = stanza.getElementsByTagNameNS(ns, 'paused')[0];
  const inactive = stanza.getElementsByTagNameNS(ns, 'inactive')[0];
  const gone = stanza.getElementsByTagNameNS(ns, 'gone')[0];
  if (composing) _callbacks.onTyping?.(from, true);
  else if (paused || inactive || gone) _callbacks.onTyping?.(from, false);
  return true;
}

function _onRosterIQ(stanza: Element): boolean {
  const iqId = stanza.getAttribute('id') ?? '';
  _connection.send($iq({ type: 'result', id: iqId }).tree());
  return true;
}

/**
 * XEP-0191 blocking push (spec §10). Acks the IQ and surfaces the affected
 * JIDs to the app via the `onBlockingPush` callback.
 */
function _onBlockingPushIQ(stanza: Element): boolean {
  const iqId = stanza.getAttribute('id') ?? '';
  const block = stanza.getElementsByTagNameNS('urn:xmpp:blocking', 'block')[0];
  const unblock = stanza.getElementsByTagNameNS('urn:xmpp:blocking', 'unblock')[0];
  const node = block ?? unblock;
  if (!node) {
    _connection.send($iq({ type: 'result', id: iqId }).tree());
    return true;
  }
  const items = node.getElementsByTagName('item');
  const jids: string[] = [];
  for (let i = 0; i < items.length; i += 1) {
    const jid = items[i].getAttribute('jid');
    if (jid) jids.push(jid);
  }
  _callbacks.onBlockingPush?.(block ? 'block' : 'unblock', jids);
  _connection.send($iq({ type: 'result', id: iqId }).tree());
  return true;
}

function _enableMessageCarbons() {
  const iq = $iq({ type: 'set' }).c('enable', { xmlns: 'urn:xmpp:carbons:2' });
  _connection.sendIQ(
    iq,
    () => console.log('[XMPP] Message carbons enabled'),
    (err: unknown) => console.warn('[XMPP] Message carbons enable failed', err),
  );
}

function _requestRoster() {
  const iq = $iq({ type: 'get' }).c('query', { xmlns: 'jabber:iq:roster' });
  _connection.sendIQ(
    iq,
    (result: Element) => {
      const items = result.getElementsByTagName('item');
      for (let i = 0; i < items.length; i += 1) {
        const jid = items[i].getAttribute('jid');
        if (jid) _connection.send($pres({ to: jid, type: 'probe' }).tree());
      }
    },
    (err: unknown) => console.warn('[XMPP] Roster request failed', err),
  );
}

function _resendPendingReceipts() {
  const ids = Object.keys(_resendReceipts);
  if (!ids.length) return;
  const parser = new DOMParser();
  ids.forEach((msgId) => {
    try {
      const xml = _resendReceipts[msgId];
      const doc = parser.parseFromString(xml, 'text/xml');
      if (doc.documentElement && _connection?.connected) {
        _connection.send(doc.documentElement);
        delete _resendReceipts[msgId];
      }
    } catch {
      delete _resendReceipts[msgId];
    }
  });
}

function _onStatusChange(status: number, condition?: string) {
  const label = STATUS_LABELS[status] ?? String(status);
  console.log(`[XMPP] Status → ${label}${condition ? ` (${condition})` : ''}`);

  if (condition === 'not-authorized') {
    _isConnecting = false;
    _shouldReconnect = false;
    _pendingStanzas.length = 0;
    _callbacks.onDisconnected?.('AUTHFAIL');
    return;
  }

  if (status === Strophe.Status.CONNECTED || status === Strophe.Status.ATTACHED) {
    _isConnecting = false;
    _reconnectAttempt = 0;
    _connection.addHandler(_onIncomingMessage, null, 'message', 'chat', null, null);
    _connection.addHandler(_onGroupMessage, null, 'message', 'groupchat', null, null);
    // MUCSub-wrapped events (spec §7.1 / §7.2). Outer wrapper has no `type`
    // attribute (or `type="headline"`) — match all message types so we can
    // unwrap and re-dispatch the inner stanza.
    _connection.addHandler(
      _onMucSubWrappedEvent,
      'http://jabber.org/protocol/pubsub#event',
      'message',
      null,
      null,
      null,
    );
    _connection.addHandler(_onCarbonMessage, 'urn:xmpp:carbons:2', 'message', null, null, null);
    _connection.addHandler(_onDeliveryReceipt, 'urn:xmpp:receipts', 'message', null, null, null);
    _connection.addHandler(_onChatMarker, 'urn:xmpp:chat-markers:0', 'message', null, null, null);
    _connection.addHandler(_onPresenceStanza, null, 'presence', null, null, null);
    _connection.addHandler(
      _onChatState,
      'http://jabber.org/protocol/chatstates',
      'message',
      null,
      null,
      null,
    );
    _connection.addHandler(_onRosterIQ, 'jabber:iq:roster', 'iq', 'set', null, null);
    // XEP-0191 blocking push (spec §10): server sends an IQ-set with a
    // `<block>` or `<unblock>` payload when another device of this user
    // toggles the block state.
    _connection.addHandler(_onBlockingPushIQ, 'urn:xmpp:blocking', 'iq', 'set', null, null);

    _enableMessageCarbons();
    _connection.send($pres().tree());
    _requestRoster();
    _resendPendingReceipts();
    _flushPendingStanzas();

    _callbacks.onConnected?.();
  }

  if (
    status === Strophe.Status.DISCONNECTED ||
    status === Strophe.Status.CONNFAIL ||
    status === Strophe.Status.CONNTIMEOUT
  ) {
    _isConnecting = false;
    if (_restartRequested) {
      _clearRestartState();
      _connection = null;
      _scheduleReconnect(true);
      return;
    }
    _callbacks.onDisconnected?.(label);
    _scheduleReconnect();
  }
  if (status === Strophe.Status.AUTHFAIL) {
    _isConnecting = false;
    _shouldReconnect = false;
    _pendingStanzas.length = 0;
    console.error('[XMPP] Auth failed');
    _callbacks.onDisconnected?.('AUTHFAIL');
  }
}

// ─── Public API ─────────────────────────────────────────────────────────────

const xmppService = {
  connect(username: string, password: string, callbacks: XmppCallbacks = {}) {
    _callbacks = callbacks;
    const nextUserId = username.includes('@') ? username.split('@')[0] : username;
    const nextJid = username.includes('@') ? username : `${username}@${DOMAIN}/${RESOURCE}`;
    const canReuse =
      _shouldReconnect &&
      _jid === nextJid &&
      _password === password &&
      Boolean(_connection?.connected || _isConnecting);

    _userId = nextUserId;
    _jid = nextJid;
    _password = password;
    _shouldReconnect = true;

    // Route changes only need different callbacks. Re-authenticating the same
    // full JID while its old WebSocket is closing causes Strophe `_connect_cb`
    // overlap and ejabberd can reject the duplicate resource.
    if (canReuse) {
      console.log(`[XMPP] Reusing connection as ${_jid}`);
      return;
    }

    console.log(`[XMPP] Connecting as ${_jid} → ${WS_URL}`);

    if (typeof window !== 'undefined') {
      window.removeEventListener('online', _resumeConnection);
      document.removeEventListener('visibilitychange', _resumeConnection);
      window.addEventListener('online', _resumeConnection);
      document.addEventListener('visibilitychange', _resumeConnection);
    }
    _openConnection();
  },

  disconnect() {
    _shouldReconnect = false;
    _isConnecting = false;
    _clearRestartState();
    if (_reconnectTimer) clearTimeout(_reconnectTimer);
    _reconnectTimer = null;
    _pendingStanzas.length = 0;
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', _resumeConnection);
      document.removeEventListener('visibilitychange', _resumeConnection);
    }
    const closingConnection = _connection;
    _connection = null;
    _closeConnection(closingConnection, 'logout');
  },

  sendMessage(recipientId: string, text: string): string | false {
    if (!_shouldReconnect) return false;
    const toJid = recipientId.includes('@') ? recipientId : `${recipientId}@${DOMAIN}`;
    const id = newMsgId();
    const stanza = $msg({ to: toJid, type: 'chat', id })
      .c('markable', { xmlns: 'urn:xmpp:chat-markers:0' })
      .up()
      .c('body')
      .t(utf8B64(text))
      .up()
      .c('subject')
      .t('chat_text')
      .up()
      .c('request', { xmlns: 'urn:xmpp:receipts' })
      .up();
    _sendOrQueue(stanza.tree(), id);
    return id;
  },

  sendGroupMessage(groupId: string, text: string): string | false {
    if (!_shouldReconnect) return false;
    const toJid = `${groupId}@${GROUP_DOMAIN}`;
    const id = newMsgId();
    const stanza = $msg({ to: toJid, type: 'groupchat', id })
      .c('markable', { xmlns: 'urn:xmpp:chat-markers:0' })
      .up()
      .c('body')
      .t(utf8B64(text))
      .up()
      .c('subject')
      .t('chat_text')
      .up();
    _sendOrQueue(stanza.tree(), id);
    return id;
  },

  sendMediaMessage(
    recipientId: string,
    subject: 'chat_image' | 'chat_audio' | 'chat_video',
    mediaPayload: MediaPayload,
    messageId?: string,
  ): string | false {
    if (!_shouldReconnect) return false;
    const toJid = recipientId.includes('@') ? recipientId : `${recipientId}@${DOMAIN}`;
    const id = messageId || newMsgId();
    const encoded = encodeMediaBody(subject, mediaPayload);
    const stanza = $msg({ to: toJid, type: 'chat', id })
      .c('markable', { xmlns: 'urn:xmpp:chat-markers:0' })
      .up()
      .c('body')
      .t(encoded)
      .up()
      .c('subject')
      .t(subject)
      .up()
      .c('request', { xmlns: 'urn:xmpp:receipts' })
      .up();
    _sendOrQueue(stanza.tree(), id);
    return id;
  },

  sendGroupMediaMessage(
    groupId: string,
    subject: 'chat_image' | 'chat_audio' | 'chat_video',
    mediaPayload: MediaPayload,
    messageId?: string,
  ): string | false {
    if (!_shouldReconnect) return false;
    const toJid = `${groupId}@${GROUP_DOMAIN}`;
    const id = messageId || newMsgId();
    const encoded = encodeMediaBody(subject, mediaPayload);
    const stanza = $msg({ to: toJid, type: 'groupchat', id })
      .c('markable', { xmlns: 'urn:xmpp:chat-markers:0' })
      .up()
      .c('body')
      .t(encoded)
      .up()
      .c('subject')
      .t(subject)
      .up();
    _sendOrQueue(stanza.tree(), id);
    return id;
  },

  sendDisplayedMarker(
    recipientId: string,
    msgId: string,
    isGroup = false,
    senderId: string | null = null,
  ) {
    const markerId = `marker-${msgId}`;
    let toJid: string;
    let msgType: 'chat' | 'groupchat';
    if (isGroup) {
      toJid = `${recipientId}@${GROUP_DOMAIN}${senderId ? `/${senderId}` : ''}`;
      msgType = 'groupchat';
    } else {
      toJid = recipientId.includes('@') ? recipientId : `${recipientId}@${DOMAIN}`;
      msgType = 'chat';
    }

    const stanza = $msg({ to: toJid, type: msgType, id: markerId })
      .c('displayed', { xmlns: 'urn:xmpp:chat-markers:0', id: msgId })
      .up()
      .c('store', { xmlns: 'urn:xmpp:hints' })
      .up();

    try {
      _resendReceipts[msgId] = new XMLSerializer().serializeToString(stanza.tree());
    } catch {
      // ignore
    }

    if (_connection?.connected) {
      _connection.send(stanza.tree());
      delete _resendReceipts[msgId];
    }
  },

  // ─── MUC-Sub (XEP-extension) — group subscription management ──────────────
  // Mirrors the Android `IQ_UserSubscription` / `IQ_UserUnSubscription` /
  // `IQ_UserUnSubscriptionMe` stanzas. See group-chat docs §11.4.

  /**
   * Subscribe a single user to a MUC room via MUC-Sub.
   *
   * Resolves on `<iq type="result"/>` and rejects on `<iq type="error"/>` or
   * timeout. Use {@link subscribeUsersToGroup} when adding many users at once.
   */
  subscribeUserToGroup(
    groupId: string,
    subscriberUserId: string | number,
    timeoutMs = 60_000,
  ): Promise<void> {
    if (!_connection?.connected) {
      return Promise.reject(new Error('XMPP not connected'));
    }
    const roomJid = `${groupId}@${GROUP_DOMAIN}`;
    const subscriberJid = `${subscriberUserId}@${DOMAIN}`;
    const elementId = `mucsub-sub-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    // Spec Flow 2 + Implementation Notes: when subscribing yourself, OMIT
    // the `jid` attribute so the server uses the authenticated session.
    // When subscribing somebody else (admin add) the `jid` is required.
    const isSelfSubscribe = String(subscriberUserId) === String(_userId);
    const subscribeAttrs: Record<string, string> = {
      xmlns: 'urn:xmpp:mucsub:0',
      nick: String(subscriberUserId),
    };
    if (!isSelfSubscribe) subscribeAttrs.jid = subscriberJid;

    const iq = $iq({ type: 'set', to: roomJid, from: _jid, id: elementId })
      .c('subscribe', subscribeAttrs)
      // Order per spec §3.2 / §8.1: messages → subscribers → affiliations.
      .c('event', { node: 'urn:xmpp:mucsub:nodes:messages' })
      .up()
      .c('event', { node: 'urn:xmpp:mucsub:nodes:subscribers' })
      .up()
      .c('event', { node: 'urn:xmpp:mucsub:nodes:affiliations' })
      .up();

    return new Promise<void>((resolve, reject) => {
      _connection.sendIQ(
        iq.tree(),
        () => resolve(),
        (errStanza: Element | null) => {
          const code = errStanza?.getElementsByTagName('error')[0]?.getAttribute('code') ?? '';
          reject(new Error(`MUC-Sub subscribe failed${code ? ` (${code})` : ''}`));
        },
        timeoutMs,
      );
    });
  },

  /**
   * Subscribe many users to a MUC room in parallel. Returns the set of users
   * that succeeded and the set that failed (with reasons) so the caller can
   * surface a partial-failure warning.
   */
  async subscribeUsersToGroup(
    groupId: string,
    subscriberUserIds: readonly (string | number)[],
    timeoutMs = 60_000,
  ): Promise<{ added: string[]; failed: { userId: string; error: string }[] }> {
    const added: string[] = [];
    const failed: { userId: string; error: string }[] = [];
    await Promise.all(
      subscriberUserIds.map(async (uid) => {
        try {
          await this.subscribeUserToGroup(groupId, uid, timeoutMs);
          added.push(String(uid));
        } catch (err) {
          failed.push({
            userId: String(uid),
            error: err instanceof Error ? err.message : 'unknown',
          });
        }
      }),
    );
    return { added, failed };
  },

  /**
   * Admin removes a subscriber from a MUC room (mirrors
   * `IQ_UserUnSubscription`). Omit `subscriberUserId` to self-leave
   * (mirrors `IQ_UserUnSubscriptionMe`).
   */
  unsubscribeUserFromGroup(
    groupId: string,
    subscriberUserId?: string | number,
    timeoutMs = 60_000,
  ): Promise<void> {
    if (!_connection?.connected) {
      return Promise.reject(new Error('XMPP not connected'));
    }
    const roomJid = `${groupId}@${GROUP_DOMAIN}`;
    const elementId = `mucsub-unsub-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    const attrs: Record<string, string> = { xmlns: 'urn:xmpp:mucsub:0' };
    if (subscriberUserId != null) {
      attrs.jid = `${subscriberUserId}@${DOMAIN}`;
    }

    const iq = $iq({ type: 'set', to: roomJid, from: _jid, id: elementId }).c('unsubscribe', attrs);

    return new Promise<void>((resolve, reject) => {
      _connection.sendIQ(
        iq.tree(),
        () => resolve(),
        (errStanza: Element | null) => {
          const code = errStanza?.getElementsByTagName('error')[0]?.getAttribute('code') ?? '';
          reject(new Error(`MUC-Sub unsubscribe failed${code ? ` (${code})` : ''}`));
        },
        timeoutMs,
      );
    });
  },

  /** Self-leave a group (alias for `unsubscribeUserFromGroup(groupId)`). */
  leaveGroup(groupId: string, timeoutMs = 60_000): Promise<void> {
    return this.unsubscribeUserFromGroup(groupId, undefined, timeoutMs);
  },

  // ─── MUC-Sub subscriber discovery (spec Flow 6) ───────────────────────────

  /**
   * Fetch the list of subscribers (members) of a single room over XMPP.
   * Mirrors spec Flow 6 — "Get All Subscribers of a Room":
   *
   *   <iq type="get" to="{groupId}@conference.{domain}">
   *     <subscriptions xmlns="urn:xmpp:mucsub:0"/>
   *   </iq>
   *
   * Returns one entry per `<subscription>` in the result, each carrying the
   * member's bare JID, room nick, and the event nodes they're currently
   * subscribed to. Useful as a live cross-check against the REST
   * `/chat/group-subs/{id}` snapshot or as a sole source of truth when REST
   * is unavailable.
   */
  fetchRoomSubscribers(
    groupId: string,
    timeoutMs = 60_000,
  ): Promise<Array<{ jid: string; nick: string; nodes: string[] }>> {
    if (!_connection?.connected) {
      return Promise.reject(new Error('XMPP not connected'));
    }
    const roomJid = `${groupId}@${GROUP_DOMAIN}`;
    const elementId = `mucsub-list-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const iq = $iq({ type: 'get', to: roomJid, from: _jid, id: elementId }).c('subscriptions', {
      xmlns: 'urn:xmpp:mucsub:0',
    });
    return new Promise((resolve, reject) => {
      _connection.sendIQ(
        iq.tree(),
        (result: Element) => {
          const subs = result.getElementsByTagNameNS('urn:xmpp:mucsub:0', 'subscription');
          const out: Array<{ jid: string; nick: string; nodes: string[] }> = [];
          for (let i = 0; i < subs.length; i += 1) {
            const s = subs[i];
            const eventEls = s.getElementsByTagName('event');
            const nodes: string[] = [];
            for (let j = 0; j < eventEls.length; j += 1) {
              const node = eventEls[j].getAttribute('node');
              if (node) nodes.push(node);
            }
            out.push({
              jid: s.getAttribute('jid') ?? '',
              nick: s.getAttribute('nick') ?? '',
              nodes,
            });
          }
          resolve(out);
        },
        (err: Element | null) => {
          const code = err?.getElementsByTagName('error')[0]?.getAttribute('code') ?? '';
          reject(new Error(`MUC-Sub list failed${code ? ` (${code})` : ''}`));
        },
        timeoutMs,
      );
    });
  },

  /**
   * Fetch every group the current user is subscribed to. Mirrors spec Flow 6
   * — "Get All Groups a User is Subscribed To":
   *
   *   <iq type="get" to="conference.{domain}">
   *     <subscriptions xmlns="urn:xmpp:mucsub:0"/>
   *   </iq>
   *
   * Useful for restoring a user's group list after a fresh install or when
   * the REST inbox snapshot is stale. Each entry carries the room JID,
   * the user's nick in that room, and the active event nodes.
   */
  fetchUserGroupSubscriptions(
    timeoutMs = 60_000,
  ): Promise<Array<{ groupId: string; jid: string; nick: string; nodes: string[] }>> {
    if (!_connection?.connected) {
      return Promise.reject(new Error('XMPP not connected'));
    }
    const elementId = `mucsub-mine-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const iq = $iq({ type: 'get', to: GROUP_DOMAIN, from: _jid, id: elementId }).c(
      'subscriptions',
      { xmlns: 'urn:xmpp:mucsub:0' },
    );
    return new Promise((resolve, reject) => {
      _connection.sendIQ(
        iq.tree(),
        (result: Element) => {
          const subs = result.getElementsByTagNameNS('urn:xmpp:mucsub:0', 'subscription');
          const out: Array<{ groupId: string; jid: string; nick: string; nodes: string[] }> = [];
          for (let i = 0; i < subs.length; i += 1) {
            const s = subs[i];
            const roomJid = s.getAttribute('jid') ?? '';
            const groupId = roomJid.includes('@') ? roomJid.split('@')[0] : roomJid;
            const eventEls = s.getElementsByTagName('event');
            const nodes: string[] = [];
            for (let j = 0; j < eventEls.length; j += 1) {
              const node = eventEls[j].getAttribute('node');
              if (node) nodes.push(node);
            }
            out.push({ groupId, jid: roomJid, nick: s.getAttribute('nick') ?? '', nodes });
          }
          resolve(out);
        },
        (err: Element | null) => {
          const code = err?.getElementsByTagName('error')[0]?.getAttribute('code') ?? '';
          reject(new Error(`MUC-Sub user-list failed${code ? ` (${code})` : ''}`));
        },
        timeoutMs,
      );
    });
  },

  /**
   * Owner destroys a MUC room (mirrors Android `IQ_ChatGroupDeleted`).
   *
   *   <iq type="set" to="{room}@conference.{domain}">
   *     <query xmlns="http://jabber.org/protocol/muc#owner">
   *       <destroy><reason>{optional}</reason></destroy>
   *     </query>
   *   </iq>
   *
   * The server then broadcasts a `<message>` with
   * `<x xmlns="http://jabber.org/protocol/muc#user"><destroy/></x>` to every
   * subscriber, which `_onGroupMessage` surfaces as a `destroyed`
   * GroupSystemEvent. Resolves on `<iq type="result"/>` and rejects on
   * `<iq type="error"/>` or timeout.
   */
  destroyGroup(groupId: string, reason?: string, timeoutMs = 60_000): Promise<void> {
    if (!_connection?.connected) {
      return Promise.reject(new Error('XMPP not connected'));
    }
    const roomJid = `${groupId}@${GROUP_DOMAIN}`;
    const elementId = `muc-destroy-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    // Spec §9: <destroy jid="{groupJid}"><reason>{reason}</reason></destroy>
    // The `jid` attribute and a non-empty `<reason>` are required by the
    // server to broadcast the destroy notification to all members.
    const query = $iq({ type: 'set', to: roomJid, from: _jid, id: elementId }).c('query', {
      xmlns: 'http://jabber.org/protocol/muc#owner',
    });
    query
      .c('destroy', { jid: roomJid })
      .c('reason')
      .t(reason ?? 'Group deleted')
      .up();

    return new Promise<void>((resolve, reject) => {
      _connection.sendIQ(
        query.tree(),
        () => resolve(),
        (errStanza: Element | null) => {
          const code = errStanza?.getElementsByTagName('error')[0]?.getAttribute('code') ?? '';
          reject(new Error(`MUC destroy failed${code ? ` (${code})` : ''}`));
        },
        timeoutMs,
      );
    });
  },

  // ─── Blocking (XEP-0191, spec §10) ────────────────────────────────────────

  /**
   * Block a user via XEP-0191:
   *
   *   <iq type="set">
   *     <block xmlns="urn:xmpp:blocking">
   *       <item jid="{userId}@localhost"/>
   *     </block>
   *   </iq>
   *
   * Per spec §10 the client must then confirm with the REST call
   * `POST /userservice/api/v3/user/block/{id}?blockToggle=true`; if the REST
   * fails the caller should call {@link unblockUser} to revert.
   */
  blockUser(targetUserId: string | number, timeoutMs = 60_000): Promise<void> {
    if (!_connection?.connected) {
      return Promise.reject(new Error('XMPP not connected'));
    }
    const targetJid = `${targetUserId}@${DOMAIN}`;
    const elementId = `block-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const iq = $iq({ type: 'set', id: elementId })
      .c('block', { xmlns: 'urn:xmpp:blocking' })
      .c('item', { jid: targetJid });
    return new Promise<void>((resolve, reject) => {
      _connection.sendIQ(
        iq.tree(),
        () => resolve(),
        (err: Element | null) => {
          const code = err?.getElementsByTagName('error')[0]?.getAttribute('code') ?? '';
          reject(new Error(`Block failed${code ? ` (${code})` : ''}`));
        },
        timeoutMs,
      );
    });
  },

  /** Unblock a previously blocked user (XEP-0191). */
  unblockUser(targetUserId: string | number, timeoutMs = 60_000): Promise<void> {
    if (!_connection?.connected) {
      return Promise.reject(new Error('XMPP not connected'));
    }
    const targetJid = `${targetUserId}@${DOMAIN}`;
    const elementId = `unblock-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const iq = $iq({ type: 'set', id: elementId })
      .c('unblock', { xmlns: 'urn:xmpp:blocking' })
      .c('item', { jid: targetJid });
    return new Promise<void>((resolve, reject) => {
      _connection.sendIQ(
        iq.tree(),
        () => resolve(),
        (err: Element | null) => {
          const code = err?.getElementsByTagName('error')[0]?.getAttribute('code') ?? '';
          reject(new Error(`Unblock failed${code ? ` (${code})` : ''}`));
        },
        timeoutMs,
      );
    });
  },

  /**
   * Fetch the server-side blocking list (XEP-0191). Returns an array of bare
   * JIDs. Called once after auth per spec §10.
   */
  fetchBlockList(timeoutMs = 60_000): Promise<string[]> {
    if (!_connection?.connected) {
      return Promise.reject(new Error('XMPP not connected'));
    }
    const elementId = `blocklist-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const iq = $iq({ type: 'get', id: elementId }).c('blocklist', {
      xmlns: 'urn:xmpp:blocking',
    });
    return new Promise<string[]>((resolve, reject) => {
      _connection.sendIQ(
        iq.tree(),
        (result: Element) => {
          const items = result.getElementsByTagName('item');
          const jids: string[] = [];
          for (let i = 0; i < items.length; i += 1) {
            const jid = items[i].getAttribute('jid');
            if (jid) jids.push(jid);
          }
          resolve(jids);
        },
        (err: Element | null) => {
          const code = err?.getElementsByTagName('error')[0]?.getAttribute('code') ?? '';
          reject(new Error(`Block-list fetch failed${code ? ` (${code})` : ''}`));
        },
        timeoutMs,
      );
    });
  },

  /**
   * Subscribe to a peer's presence and immediately probe the current state.
   *
   * MyU users are not added to the XMPP roster automatically, so without
   * this the server only emits presence pushes for contacts the peer has
   * pre-subscribed us to — which is rarely the case. We:
   *   1. Send `<presence type="subscribe"/>` so future presence updates
   *      from the peer reach us (the peer side auto-accepts via
   *      `_onPresenceStanza`'s `subscribe` handler).
   *   2. Send `<presence type="probe"/>` to fetch the current state right
   *      away — without this the green dot only lights up after the peer
   *      next changes status.
   *
   * Safe to call repeatedly; the server collapses duplicate subscriptions.
   * Skips the current user and any non-numeric / empty ids.
   */
  probePeerPresence(peerIds: ReadonlyArray<string | number>): void {
    if (!_connection?.connected) return;
    const seen = new Set<string>();
    for (const raw of peerIds) {
      const id = String(raw ?? '').trim();
      if (!id || id === _userId) continue;
      if (seen.has(id)) continue;
      seen.add(id);
      const to = id.includes('@') ? id : `${id}@${DOMAIN}`;
      try {
        _connection.send($pres({ to, type: 'subscribe' }).tree());
        _connection.send($pres({ to, type: 'probe' }).tree());
      } catch {
        // best-effort
      }
    }
  },

  get isConnected(): boolean {
    return !!_connection?.connected;
  },

  get jid(): string {
    return _jid;
  },

  get userId(): string {
    return _userId;
  },

  get groupDomain(): string {
    return GROUP_DOMAIN;
  },
};

export default xmppService;
